import { call, put, select, takeLatest, all, fork } from 'redux-saga/effects';
import {
  globalCacheActions,
  selectIsEventsCacheValid,
  selectIsParentMomentsCacheValid
} from './globalCacheSlice';
import {
  fetchEventsCached,
  fetchParentMomentsCached
} from '../services/momentsApi';
import { milestoneAttributesService } from '../services/milestoneAttributesService';
import type { EventData, ParentMomentData } from '../services/momentsApi';

// Worker saga for loading events
function* loadEventsSaga() {
  try {
    // Check if cache is still valid
    const isCacheValid: boolean = yield select(selectIsEventsCacheValid);

    if (isCacheValid) {
      // Cache is still valid, no need to fetch
      return;
    }

    const response: {
      success: boolean;
      data: EventData[];
      error?: string;
      message?: string;
    } = yield call(fetchEventsCached);

    if (response.success && response.data) {
      yield put(globalCacheActions.eventsLoaded(response.data));
    } else {
      const errorMessage =
        response.error || response.message || 'Failed to load events';
      yield put(globalCacheActions.eventsLoadError(errorMessage));
      console.warn('Failed to load events globally:', errorMessage);
    }
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : 'Unknown error loading events';
    yield put(globalCacheActions.eventsLoadError(errorMessage));
    console.error('Error in loadEventsSaga:', error);
  }
}

// Worker saga for loading parent moments
function* loadParentMomentsSaga() {
  try {
    // Check if cache is still valid
    const isCacheValid: boolean = yield select(selectIsParentMomentsCacheValid);

    if (isCacheValid) {
      // Cache is still valid, no need to fetch
      return;
    }

    const response: {
      success: boolean;
      data: ParentMomentData[];
      error?: string;
      message?: string;
    } = yield call(fetchParentMomentsCached);

    if (response.success && response.data) {
      yield put(globalCacheActions.parentMomentsLoaded(response.data));
    } else {
      const errorMessage =
        response.error || response.message || 'Failed to load parent moments';
      yield put(globalCacheActions.parentMomentsLoadError(errorMessage));
      console.warn('Failed to load parent moments globally:', errorMessage);
    }
  } catch (error) {
    const errorMessage =
      error instanceof Error
        ? error.message
        : 'Unknown error loading parent moments';
    yield put(globalCacheActions.parentMomentsLoadError(errorMessage));
    console.error('Error in loadParentMomentsSaga:', error);
  }
}

// ✅ NEW: Worker saga for force refreshing parent moments (bypasses cache check)
function* forceRefreshParentMomentsSaga() {
  try {
    // Call fetchParentMomentsCached with forceRefresh=true to bypass cache
    const response: {
      success: boolean;
      data: ParentMomentData[];
      error?: string;
      message?: string;
    } = yield call(fetchParentMomentsCached, true); // ✅ forceRefresh=true

    if (response.success && response.data) {
      yield put(globalCacheActions.parentMomentsLoaded(response.data));
    } else {
      const errorMessage =
        response.error || response.message || 'Failed to load parent moments';
      yield put(globalCacheActions.parentMomentsLoadError(errorMessage));
    }
  } catch (error) {
    const errorMessage =
      error instanceof Error
        ? error.message
        : 'Unknown error force refreshing parent moments';
    yield put(globalCacheActions.parentMomentsLoadError(errorMessage));
    console.error('Error in forceRefreshParentMomentsSaga:', error);
  }
}

// Worker saga for loading milestone attributes
function* loadMilestoneAttributesSaga() {
  try {
    const milestoneAttributes = yield call([
      milestoneAttributesService,
      'getMilestoneAttributes'
    ]);

    yield put(
      globalCacheActions.milestoneAttributesLoaded(milestoneAttributes)
    );
  } catch (error) {
    console.error('Error loading milestone attributes:', error);
    const errorMessage =
      error instanceof Error
        ? error.message
        : 'Failed to load milestone attributes';
    yield put(globalCacheActions.milestoneAttributesLoadError(errorMessage));
  }
}

// Worker saga for loading all global cache data
function* loadGlobalCacheDataSaga() {
  try {
    // Run all loads in parallel for optimal performance
    yield all([
      call(loadEventsSaga),
      call(loadParentMomentsSaga),
      call(loadMilestoneAttributesSaga)
    ]);
  } catch (error) {
    console.error('Error in loadGlobalCacheDataSaga:', error);
  }
}

// Watcher sagas
function* watchLoadEvents() {
  yield takeLatest(globalCacheActions.loadEvents.type, loadEventsSaga);
}

function* watchLoadParentMoments() {
  yield takeLatest(
    globalCacheActions.loadParentMoments.type,
    loadParentMomentsSaga
  );
}

// ✅ NEW: Watcher for force refresh parent moments
function* watchForceRefreshParentMoments() {
  yield takeLatest(
    globalCacheActions.forceRefreshParentMoments.type,
    forceRefreshParentMomentsSaga
  );
}

function* watchLoadMilestoneAttributes() {
  yield takeLatest(
    globalCacheActions.loadMilestoneAttributes.type,
    loadMilestoneAttributesSaga
  );
}

function* watchLoadGlobalCacheData() {
  yield takeLatest(
    globalCacheActions.loadGlobalCacheData.type,
    loadGlobalCacheDataSaga
  );
}

// Root saga
export function* globalCacheSaga() {
  yield all([
    fork(watchLoadEvents),
    fork(watchLoadParentMoments),
    fork(watchForceRefreshParentMoments), // ✅ NEW: Watch for force refresh
    fork(watchLoadMilestoneAttributes),
    fork(watchLoadGlobalCacheData)
  ]);
}

export default globalCacheSaga;
